import React, { useMemo, useState } from 'react';
import {
  PROGRAMS, listsForProgram, rowsForLists, columnsForExport,
  buildCsv, exportFileName, downloadCsv,
} from './crmExport';

// Export CRM members as a CSV file: the whole CRM, one program (optionally
// narrowed to some of its lists), or one list. Built in the browser from
// the data the dashboard has already loaded.
export default function CrmCsvExport({ lists = [], selectedLists = [], columns = [], rows = [], onClose }) {
  const [scope, setScope] = useState(selectedLists.length === 1 ? 'list' : 'all');
  const [listName, setListName] = useState(selectedLists[0] || lists[0] || '');
  const [programId, setProgramId] = useState('siher');
  const [unchecked, setUnchecked] = useState([]);

  const programLists = useMemo(() => listsForProgram(programId, lists), [programId, lists]);
  const exportLists = useMemo(() => {
    if (scope === 'list') return listName ? [listName] : [];
    if (scope === 'program') return programLists.filter((name) => !unchecked.includes(name));
    return [];
  }, [scope, listName, programLists, unchecked]);

  const exportRows = useMemo(() => rowsForLists(rows, columns, exportLists), [rows, columns, exportLists]);
  const exportCols = useMemo(() => columnsForExport(columns, exportLists), [columns, exportLists]);
  const blocked = scope !== 'all' && exportLists.length === 0;

  const scopeLabel = scope === 'all' ? 'all-members' : scope === 'list' ? listName : programId;

  const toggleList = (name) => setUnchecked((prev) =>
    prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]);

  const handleDownload = () => {
    downloadCsv(buildCsv(exportRows, exportCols), exportFileName(scopeLabel));
    onClose();
  };

  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal" style={{ width: 560 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-title">Export CSV</div>
        <div className="modal-sub">Download members as a spreadsheet file, to open in Excel or Google Sheets.</div>

        <label className="mlabel">What to export</label>
        <select className="mselect" style={{ width: '100%', marginBottom: 14 }} value={scope}
          onChange={(e) => { setScope(e.target.value); setUnchecked([]); }}>
          <option value="all">Entire CRM</option>
          <option value="program">A program</option>
          <option value="list">One list</option>
        </select>

        {scope === 'program' && (
          <>
            <label className="mlabel">Program</label>
            <select className="mselect" style={{ width: '100%', marginBottom: 10 }} value={programId}
              onChange={(e) => { setProgramId(e.target.value); setUnchecked([]); }}>
              {PROGRAMS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
            {programLists.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 14 }}>No lists belong to this program yet.</div>
            ) : (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 12, color: 'var(--muted2)', marginBottom: 6 }}>Uncheck any lists to leave them out.</div>
                {programLists.map((name) => (
                  <label key={name} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 4, cursor: 'pointer' }}>
                    <input type="checkbox" checked={!unchecked.includes(name)} onChange={() => toggleList(name)} />
                    {name}
                  </label>
                ))}
              </div>
            )}
          </>
        )}

        {scope === 'list' && (
          <>
            <label className="mlabel">List</label>
            {lists.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 14 }}>There are no lists yet.</div>
            ) : (
              <select className="mselect" style={{ width: '100%', marginBottom: 14 }} value={listName}
                onChange={(e) => setListName(e.target.value)}>
                {lists.map((name) => <option key={name} value={name}>{name}</option>)}
              </select>
            )}
          </>
        )}

        <div style={{ fontSize: 12, color: 'var(--muted2)', margin: '4px 0 10px' }}>
          {exportRows.length} member{exportRows.length === 1 ? '' : 's'}, {exportCols.length} column{exportCols.length === 1 ? '' : 's'}
        </div>
        <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 4 }}>
          This file contains members' personal details. Please store it securely and delete it when you're done.
        </div>

        <div className="mactions">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-solid" onClick={handleDownload} disabled={blocked || exportRows.length === 0}>
            Download CSV
          </button>
        </div>
      </div>
    </div>
  );
}
